import type { CompanyCard } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// WHERE A SEQUENCE ACTUALLY STANDS, ON THE LIST OF ACCOUNTS.
//
// Kyle: "how can I see what's active/pending/draft?"
//
// The honest answer was: you couldn't. Today answers "what is queued", Schedule
// answers "what goes out next", and the account record answers it for ONE
// account. Nothing answered it across the pipeline, so the question cost a click
// into every account in turn.
//
// THE DATA WAS ALREADY ON THE WIRE. `sequence_status` has been selected by the
// board query and shipped to the browser this whole time, and NOTHING rendered
// it. The table's "Sequence" column showed `2/18` — written over total — which
// makes a draft nobody has scheduled look exactly like a cadence that is
// currently mailing people. Only `touches_sent` is new here.
//
// It is a LINE, not a new screen. Same move as every good change in this app:
// the answer belongs on the surface you already open, not behind a sixth place
// to look. "Payra · active · 1 of 18 sent" has answered the question before you
// click anything.

/** Rank for sorting: what is actually moving comes first, dormant last. */
const SEQ_RANK: Record<string, number> = {
  active: 0, armed: 1, paused: 2, draft: 3, cancelled: 4, completed: 5,
};

export function seqRank(c: CompanyCard): number {
  return c.sequence_status ? (SEQ_RANK[c.sequence_status] ?? 6) : 7;
}

/**
 * One sentence about a sequence, and the tone it deserves.
 *
 * ARMED AND ACTIVE ARE DIFFERENT AND MUST LOOK DIFFERENT. Armed means dated and
 * waiting; active means mail has already gone to these people. Collapsing them
 * into one green chip loses the only distinction that changes what you do next.
 */
export function seqState(c: CompanyCard): { label: string; detail: string; tone: string } {
  const st = c.sequence_status;
  // NUMBERS, NOT WHATEVER THE DRIVER FELT LIKE. Postgres `count(*)` is a bigint
  // and the Neon driver returns bigints as STRINGS, so these arrive as '18' and
  // '0' from the board query — while the account-detail path builds them with
  // .filter().length and returns real numbers. `written === 0` was therefore
  // false for a draft with no copy at all, and the one state worth flagging
  // never rendered. Caught against live data, not by reading this.
  const total = Number(c.touches_total) || 0;
  const written = Number(c.touches_written) || 0;
  const sent = Number(c.touches_sent) || 0;

  if (!st) return { label: 'no sequence', detail: '', tone: 'none' };
  if (st === 'paused') return { label: 'paused', detail: `${sent} of ${total} sent`, tone: 'paused' };
  if (st === 'active') return { label: 'active', detail: `${sent} of ${total} sent`, tone: 'live' };
  if (st === 'armed') {
    return { label: 'armed', detail: `${total} dated, none sent yet`, tone: 'armed' };
  }
  if (st === 'draft') {
    // A draft with no copy and a draft ready to arm are different problems.
    return {
      label: 'draft',
      detail: total ? `${written} of ${total} written` : 'nothing planned',
      tone: written === 0 ? 'empty' : 'draft',
    };
  }
  return { label: st, detail: '', tone: 'none' };
}
